// Chat: the last 8 lines (fading after 8 s) and the input line. Lines are pooled DOM nodes.

import { div, setClass, span } from "./dom";
import type { ChatMessage } from "./types";

const MAX_LINES = 8;
const FADE_AFTER_MS = 8000;
const MAX_LENGTH = 200;
/** Typing that arrives this soon after opening is the key press that opened the chat. */
const OPEN_SWALLOW_MS = 40;

interface Line {
  root: HTMLDivElement;
  tag: HTMLSpanElement;
  name: HTMLSpanElement;
  text: HTMLSpanElement;
  bornMs: number;
  /** In the log and not faded yet. */
  live: boolean;
}

export class Chat {
  readonly root = div("hud-chat");
  private readonly log = div("hud-chat__log");
  private readonly form = div("hud-chat__input");
  private readonly labelEl = span("hud-chat__label");
  private readonly input = document.createElement("input");
  private readonly lines: Line[] = [];
  private next = 0;
  private pending = 0;

  private opened = false;
  private openedAt = 0;
  private teamOnly = false;
  private teamCapable = true;
  private onSend: ((text: string, teamOnly: boolean) => void) | null = null;

  constructor() {
    for (let i = 0; i < MAX_LINES; i++) {
      const line: Line = {
        root: div("hud-chat__line"),
        tag: span("hud-chat__tag", "TEAM"),
        name: span("hud-chat__name"),
        text: span("hud-chat__text"),
        bornMs: 0,
        live: false,
      };
      line.root.hidden = true;
      line.root.append(line.tag, line.name, line.text);
      this.lines.push(line);
      this.log.append(line.root);
    }
    this.input.type = "text";
    this.input.maxLength = MAX_LENGTH;
    this.input.autocomplete = "off";
    this.input.spellcheck = false;
    this.input.className = "hud-chat__field";
    this.input.setAttribute("aria-label", "Chat message");
    this.input.addEventListener("keydown", (e) => this.onKey(e));
    this.input.addEventListener("keyup", (e) => e.stopPropagation());
    this.input.addEventListener("keypress", (e) => e.stopPropagation());
    this.input.addEventListener("beforeinput", (e) => {
      if (performance.now() - this.openedAt < OPEN_SWALLOW_MS) e.preventDefault();
    });
    this.input.addEventListener("blur", () => this.close());
    this.form.append(this.labelEl, this.input);
    this.form.hidden = true;
    this.root.append(this.log, this.form);
  }

  /** Whether team chat exists in this match (team modes with an assigned team). */
  setTeamCapable(v: boolean): void {
    this.teamCapable = v;
    if (this.opened && !v && this.teamOnly) this.setTeamOnly(false);
  }

  push(m: ChatMessage, nowMs: number): void {
    const line = this.lines[this.next]!;
    this.next = (this.next + 1) % MAX_LINES;
    if (!line.live) this.pending++;
    line.live = true;
    line.bornMs = nowMs;
    line.tag.hidden = !m.teamOnly;
    line.tag.className = `hud-chat__tag hud-team-${m.team}`;
    line.name.className = `hud-chat__name hud-team-${m.team}`;
    line.name.textContent = m.name;
    line.text.textContent = m.text;
    setClass(line.root, "is-faded", false);
    line.root.hidden = false;
    this.log.append(line.root); // move to the end: newest line at the bottom
  }

  open(teamOnly: boolean, onSend: (text: string, teamOnly: boolean) => void, nowMs: number): void {
    this.onSend = onSend;
    this.opened = true;
    this.openedAt = nowMs;
    this.input.value = "";
    this.setTeamOnly(teamOnly && this.teamCapable);
    this.form.hidden = false;
    this.root.classList.add("is-open");
    this.input.focus();
  }

  close(): void {
    if (!this.opened) return;
    this.opened = false;
    this.onSend = null;
    this.form.hidden = true;
    this.root.classList.remove("is-open");
    this.input.value = "";
    if (document.activeElement === this.input) this.input.blur();
  }

  isOpen(): boolean {
    return this.opened;
  }

  /** Per frame: fade old lines. Nothing to do when every line already faded. */
  update(nowMs: number): void {
    if (this.pending === 0 || this.opened) return;
    for (let i = 0; i < MAX_LINES; i++) {
      const l = this.lines[i]!;
      if (l.live && nowMs - l.bornMs > FADE_AFTER_MS) {
        l.live = false;
        this.pending--;
        l.root.classList.add("is-faded");
      }
    }
  }

  private setTeamOnly(v: boolean): void {
    this.teamOnly = v;
    this.labelEl.textContent = v ? "TEAM" : "ALL";
    setClass(this.form, "is-team", v);
  }

  private onKey(e: KeyboardEvent): void {
    e.stopPropagation(); // the game must not see typing
    if (e.isComposing) return;
    if (e.key === "Enter") {
      e.preventDefault();
      const text = this.input.value.trim();
      const cb = this.onSend;
      const team = this.teamOnly;
      this.close();
      if (text !== "" && cb) cb(text, team);
    } else if (e.key === "Escape") {
      e.preventDefault();
      this.close();
    } else if (e.key === "Tab") {
      e.preventDefault();
      if (this.teamCapable) this.setTeamOnly(!this.teamOnly);
    }
  }
}
